// Top-words bar chart — alternative view to LabelCloudBody for freeform
// features. Same tokenizer, but rendered as a horizontal bar chart of the
// top N tokens so exact counts are readable.

import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { tokenize } from './tokenize';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';

interface ValueRow {
  date: string;
  freeform_value: string | null;
}

const TOP_N_INLINE = 12;
const TOP_N_FULLSCREEN = 25;
const BAR_PX = 22;

const TopWordsBody: React.FC<ChartBodyProps> = ({ featureId, fullscreen }) => {
  const [rows, setRows] = useState<ValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<ValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load top words');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const dataDates = useMemo(() => rows.map((r) => new Date(r.date).getTime()), [rows]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const topN = fullscreen ? TOP_N_FULLSCREEN : TOP_N_INLINE;
  const tokens = useMemo(() => {
    const filtered = rows.filter((r) => withinWindow(new Date(r.date).getTime(), win));
    return tokenize(filtered, topN);
  }, [rows, topN, win]);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No words recorded yet for this feature.</p>
      </div>
    );
  }

  // Fixed bar height; inline mode lives inside a card so we cap to 320px
  // and let recharts handle squeeze. Fullscreen takes whatever's there.
  const inlineHeight = Math.min(320, Math.max(80, tokens.length * BAR_PX + 24));

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      {tokens.length === 0 ? (
        <div className="flex items-center justify-center text-gray-500 py-8">
          <p>No words in this period.</p>
        </div>
      ) : (
        <div className={fullscreen ? 'flex-1 min-h-0' : ''} style={{ height: fullscreen ? undefined : inlineHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={tokens}
              margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
            >
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} />
              <YAxis
                type="category"
                dataKey="word"
                tick={{ fontSize: 13 }}
                width={100}
                interval={0}
                tickLine={false}
              />
              <Tooltip
                formatter={(value: number) => [`${value}`, 'Count']}
                cursor={false}
              />
              <Bar dataKey="count" fill="#8884d8" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default TopWordsBody;
